<script setup lang="ts">
import PdsButton from '@/components/pds/PdsButton.vue';
import PdsField from '@/components/pds/PdsField.vue';
import PdsSegmented from '@/components/pds/PdsSegmented.vue';
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { agentUrlIssue } from '../print/agent.url';
import { detectOs } from '../print/agent.download';
import { isNative } from '../print/native.bridge';
import type { FoundPrinter } from '../print/printer.agent';
import type { PaperWidth, PrinterTransport } from '../print/printer.settings';
import { usePrinter } from '../print/usePrinter';
import AgentDownload from './AgentDownload.vue';
import LabeledPanel from './LabeledPanel.vue';

const {
  settings,
  configured,
  state,
  message,
  found,
  scanning,
  scanned,
  printing,
  check,
  scan,
  choose,
  printTest,
} = usePrinter();

const TRANSPORTS: { value: PrinterTransport; label: string }[] = [
  { value: 'network', label: 'Réseau' },
  { value: 'usb', label: 'USB' },
];
const PAPERS: { value: PaperWidth; label: string }[] = [
  { value: '80mm', label: '80 mm' },
  { value: '58mm', label: '58 mm' },
];

const tested = ref<'ok' | 'failed' | null>(null);

// Dans l'application tablette, l'agent est l'application : ni téléchargement ni adresse d'agent, et pas d'USB.
const native = isNative();

// Arrivé depuis « Rechercher sur le réseau » : on part de l'adresse d'agent saisie et la recherche se lance seule.
const route = useRoute();
onMounted(() => {
  if (native) settings.transport = 'network';
  const agent = route.query.agent;
  if (typeof agent === 'string' && agent) settings.agentUrl = agent;
  if (route.query.scan === '1') void scan();
});

const urlIssue = computed(() =>
  agentUrlIssue(settings.agentUrl, {
    pageProtocol: window.location.protocol,
    isMobile: detectOs(navigator) === null,
  }),
);

// Le port se saisit comme du texte mais se garde comme un nombre ; une saisie illisible garde l'ancien.
const portText = computed({
  get: () => String(settings.port),
  set: (value: string) => {
    const port = Number(value);
    if (Number.isInteger(port) && port > 0 && port < 65536)
      settings.port = port;
  },
});

const addressOf = (device: FoundPrinter) =>
  device.address
    ? `${device.address}${device.meta?.port ? `:${device.meta.port}` : ''}`
    : (device.meta?.printerName ?? device.name);

const isChosen = (device: FoundPrinter) =>
  settings.transport === 'network'
    ? device.address === settings.host
    : (device.meta?.printerName ?? device.name) === settings.printerName;

const test = async () => {
  tested.value = (await printTest()) ? 'ok' : 'failed';
};

const STATE_TEXT = {
  unconfigured: 'Aucune imprimante choisie',
  unverified: 'À vérifier',
  checking: 'Vérification…',
  online: 'Connectée',
  offline: 'Hors ligne',
} as const;
</script>

<template>
  <LabeledPanel label="Imprimante">
    <div class="no-scrollbar flex min-h-0 flex-col gap-5 overflow-y-auto">
      <p
        v-if="native"
        class="text-encre-douce text-[12px] leading-[17px]"
        data-testid="native-hint"
      >
        L’impression passe par l’application de la tablette. Recherchez
        l’imprimante sur le réseau, ou saisissez son adresse IP ci-dessous.
      </p>
      <template v-else>
        <p class="text-encre-douce text-[12px] leading-[17px]">
          L’impression passe par l’agent installé sur le PC relié à
          l’imprimante, comme à la caisse. Il doit être démarré.
        </p>

        <AgentDownload />

        <PdsField
          id="printer-agent"
          v-model="settings.agentUrl"
          label="Adresse de l’agent"
          placeholder="http://localhost:4310"
        />
        <p
          v-if="urlIssue"
          role="alert"
          class="text-alerte-texte -mt-3 text-[12px] leading-[17px]"
          data-testid="agent-url-issue"
        >
          {{ urlIssue.text }}
        </p>
        <p v-else class="text-encre-douce -mt-3 text-[12px] leading-[17px]">
          Depuis une tablette, saisissez l’adresse du PC (par exemple
          http://192.168.1.10:4310) : l’agent l’affiche au démarrage. Le PC et
          la tablette doivent être sur le même réseau.
        </p>
      </template>

      <div class="flex flex-wrap items-center gap-3">
        <PdsSegmented
          v-if="!native"
          v-model="settings.transport"
          :options="TRANSPORTS"
        />
        <PdsSegmented v-model="settings.paperWidth" :options="PAPERS" />
      </div>

      <div class="flex flex-col gap-3">
        <PdsButton variant="second" :loading="scanning" @click="scan">
          {{
            settings.transport === 'network'
              ? 'Rechercher sur le réseau'
              : 'Rechercher les imprimantes USB'
          }}
        </PdsButton>
        <p
          v-if="scanned && !scanning && !found.length && !message"
          class="text-encre-douce text-[12px] leading-[17px]"
        >
          Aucune imprimante trouvée. Saisissez son adresse ci-dessous.
        </p>
        <ul
          v-if="found.length"
          class="flex flex-col"
          aria-label="Imprimantes trouvées"
        >
          <li
            v-for="device in found"
            :key="device.id"
            class="border-filet flex items-center gap-[14px] border-t py-3 first:border-t-0"
          >
            <div class="min-w-0 flex-1">
              <p class="truncate text-[14px] leading-[20px] font-semibold">
                {{ device.name }}
              </p>
              <p class="text-encre-douce truncate text-[12px] leading-[17px]">
                {{ addressOf(device) }}
              </p>
            </div>
            <div class="min-w-[96px]">
              <PdsButton
                variant="second"
                :disabled="isChosen(device) && state === 'online'"
                @click="choose(device)"
              >
                {{
                  isChosen(device) && state === 'online' ? 'Choisie' : 'Choisir'
                }}
              </PdsButton>
            </div>
          </li>
        </ul>
      </div>

      <div
        class="grid grid-cols-[1fr_110px] gap-3"
        v-if="settings.transport === 'network'"
      >
        <PdsField
          id="printer-host"
          v-model="settings.host"
          label="Adresse IP"
          placeholder="192.168.1.24"
        />
        <PdsField id="printer-port" v-model="portText" label="Port" />
      </div>
      <PdsField
        v-else
        id="printer-name"
        v-model="settings.printerName"
        label="Nom de l’imprimante"
        placeholder="EPSON TM-T20"
      />

      <div class="flex flex-wrap items-center gap-3">
        <span
          class="inline-flex h-7 items-center rounded-[8px] px-[10px] text-[12px] font-semibold"
          :class="
            state === 'online'
              ? 'bg-succes-doux text-succes-texte'
              : state === 'offline'
                ? 'bg-alerte-doux text-alerte-texte'
                : 'bg-creux text-encre-douce'
          "
        >
          {{ STATE_TEXT[state] }}
        </span>
        <div class="min-w-[110px]">
          <PdsButton variant="second" :disabled="!configured" @click="check">
            Vérifier
          </PdsButton>
        </div>
        <div class="min-w-[170px]">
          <PdsButton :disabled="!configured" :loading="printing" @click="test">
            Imprimer un test
          </PdsButton>
        </div>
      </div>

      <p
        v-if="message"
        role="alert"
        class="text-alerte-texte text-[12px] leading-[17px]"
      >
        {{ message }}
      </p>
      <p
        v-else-if="tested === 'ok'"
        role="status"
        class="text-succes-texte text-[12px] leading-[17px]"
      >
        Ticket de test envoyé.
      </p>
    </div>
  </LabeledPanel>
</template>
